import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { useFormContext } from 'react-hook-form'
import { useSelector } from 'react-redux'
import { Grid, Stack, TextField, Typography } from '@mui/material'
import { makeStyles } from '@mui/styles'
import { useGridApiRef } from '@mui/x-data-grid-pro'
import { useGetCurrencyConversionsQuery } from 'services/currencyConversions'
import BasicDataGrid from 'components/generic/datagrid'
import EditableCell from 'components/generic/datagrid/cells/EditableCell'
import InputFormat from 'components/generic/inputs/formatting/InputFormat'
import { dateFormatter } from 'utils/data-manipulation/valueFormatter'

const useStyles = makeStyles(theme => ({
  root: {
    // remove header padding to align headers with column values
    '& .MuiDataGrid-columnHeaderTitleContainer': {
      padding: theme.spacing(0),
    },
    '& .MuiDataGrid-columnHeader': {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
    },
    '& .MuiDataGrid-cell': {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
    },
    '& .MuiDataGrid-columnSeparator': {
      visibility: 'hidden',
    },
    '& .MuiDataGrid-columnHeader:focus': {
      outline: 'none',
    },
    '& .MuiDataGrid-cell:focus': {
      outline: 'none',
    },
    '& .MuiDataGrid-cell:focus-within': {
      outline: 'none',
    },
  },
}))

const generateColumns = ({ apiRef, control }) => [
  {
    field: 'currency',
    headerName: 'Currency',
    flex: 4,
    headerAlign: 'left',
    align: 'left',
    editable: false,
    sortable: false,
    valueGetter: ({ row = {} }) => {
      const { id, currency } = row
      return `${currency}${id === 'default' ? ' (Default)' : ''}`
    },
  },
  {
    field: 'conversion',
    headerName: 'Conversion',
    minWidth: 150,
    headerAlign: 'right',
    align: 'right',
    editable: false,
    sortable: false,
    type: 'number',
    renderCell: ({ row = {} }) => {
      const { id, conversion } = row
      return (
        <EditableCell
          name={`overviewSettings.overview.conversions.${id}.conversion`}
          control={control}
          defaultValue={conversion}
          isEdit
          size="small"
          onChange={event => {
            const { value } = event.target
            apiRef.current?.updateRows([{ ...row, conversion: value }])
          }}
          disabled={id === 'default'}
          InputProps={{
            disableUnderline: true,
            inputComponent: InputFormat,
            inputProps: {
              decimalScale: 6,
              style: {
                fontSize: '0.875rem',
                textAlign: 'right',
              },
            },
          }}
        />
      )
    },
  },
  {
    field: 'effectiveDate',
    headerName: 'Last Updated',
    flex: 4,
    headerAlign: 'left',
    align: 'left',
    editable: false,
    sortable: false,
    valueFormatter: ({ value }) => (value ? dateFormatter(new Date(value)) : '--'),
  },
]

const SettingsCurrencyConversion = ({ title, subTitle, subTitle2 }) => {
  const { control } = useFormContext()
  const apiRef = useGridApiRef()
  const classes = useStyles()
  const columns = generateColumns({ apiRef, control })
  const { defaultCurrency } = useSelector(state => state.settings)

  const { data: currencyConversionsData } = useGetCurrencyConversionsQuery({})

  const currencyConversionRows = useMemo(() => {
    if (!currencyConversionsData)
      return [{ id: 'default', currency: defaultCurrency, conversion: 1, effectiveDate: null }]
    const formattedExchangeRates =
      currencyConversionsData?.exchanges?.map(exchangeRate => ({
        id: exchangeRate.id,
        currency: exchangeRate.fc,
        conversion: exchangeRate.rate,
        effectiveDate: exchangeRate.effectiveDate,
      })) || []
    return [
      ...[{ id: 'default', currency: defaultCurrency, conversion: 1, effectiveDate: null }],
      ...formattedExchangeRates,
    ]
  }, [currencyConversionsData])

  return (
    <>
      <Typography variant="h6" color="textPrimary">
        {title}
      </Typography>
      <Grid mt={2} mb={1}>
        <Stack alignItems="center" direction="row">
          <TextField label="Default Currency" value={defaultCurrency} disabled sx={{ width: '200px' }} />
        </Stack>
      </Grid>
      <Typography variant="body2" color="textSecondary">
        {subTitle}
      </Typography>
      {subTitle2 && (
        <Typography variant="body2" color="textSecondary">
          {subTitle2}
        </Typography>
      )}
      <Grid mt={3}>
        <BasicDataGrid
          className={classes.root}
          density="standard"
          height="auto"
          rows={currencyConversionRows}
          columns={columns}
          apiRef={apiRef}
          editMode="cell"
          disableColumnMenu
          hideFooter
          onCellClick={(_, event) => event.stopPropagation()}
        />
      </Grid>
    </>
  )
}

SettingsCurrencyConversion.propTypes = {
  title: PropTypes.string.isRequired,
  subTitle: PropTypes.string.isRequired,
  subTitle2: PropTypes.string,
}

SettingsCurrencyConversion.defaultProps = {
  subTitle2: '',
}

export default SettingsCurrencyConversion
